// Journal d'audit — la pièce qu'on produit en défense.
//
// Deux niveaux, et la différence entre les deux est capitale :
//
//   • **Le journal d'un dossier** : chaque entrée est authentifiée (hash +
//     signature). Mais les entrées d'un dossier ne se suivent pas dans la chaîne
//     du cabinet — celles des autres dossiers s'intercalent. On ne peut donc rien
//     dire des trous : ce niveau prouve ce qu'on montre, pas qu'on montre tout.
//
//   • **La chaîne du cabinet** : contiguë. C'est elle, et elle seule, qui prouve
//     qu'**aucune entrée n'a été supprimée**.
//
// L'interface ne mélange pas les deux, parce qu'un avocat qui croit prouver
// l'exhaustivité alors qu'il ne prouve que l'authenticité se fera démonter à
// l'audience.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/store';
import {
  checkPin,
  exportBundle,
  fetchDossierAudit,
  fetchTenantChain,
  libelleAction,
  verifyBundle,
  type AuditBundle,
  type ChainVerdict,
} from './audit';

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

const horodatage = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

/** Le journal d'un dossier, dans la vue du dossier. */
export function DossierAudit({ dossierId }: { dossierId: string }) {
  return <AuditView scope="dossier" dossierId={dossierId} />;
}

/** La chaîne complète du cabinet, dans le panneau de session. */
export function CabinetAudit() {
  const [ouvert, setOuvert] = useState(false);
  if (!ouvert) {
    return (
      <section className="mt-6 rounded border border-slate-200 p-4 text-left">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Journal du cabinet
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          La chaîne complète, contiguë — la seule qui prouve qu&apos;aucune entrée
          n&apos;a été supprimée. C&apos;est elle qu&apos;on produit devant le
          bâtonnier.
        </p>
        <button
          type="button"
          onClick={() => setOuvert(true)}
          data-testid="open-cabinet-audit"
          className="mt-3 w-full rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Ouvrir et vérifier
        </button>
      </section>
    );
  }
  return <AuditView scope="tenant" />;
}

// ---------------------------------------------------------------------------

function AuditView({
  scope,
  dossierId,
}: {
  scope: 'dossier' | 'tenant';
  dossierId?: string;
}) {
  const session = useAuth((s) => s.session);
  const masterKey = useAuth((s) => s.masterKey);
  const tenantSlug = useAuth((s) => s.tenantSlug);

  const [bundle, setBundle] = useState<AuditBundle | null>(null);
  const [verdict, setVerdict] = useState<ChainVerdict | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  const charger = useCallback(async () => {
    if (!session || !masterKey) return;
    setBusy(true);
    setError(null);
    try {
      const b =
        scope === 'tenant'
          ? await fetchTenantChain(session.sessionToken)
          : await fetchDossierAudit(session.sessionToken, dossierId!);

      // L'épingle AVANT la vérification : sans elle, vérifier des signatures avec
      // une clé fournie par le serveur ne prouve rien du tout.
      const pin = checkPin(tenantSlug, masterKey.masterKey, b.signingPublicKey);

      setBundle(b);
      setVerdict(verifyBundle(b, pin));
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  }, [session, masterKey, tenantSlug, scope, dossierId]);

  useEffect(() => {
    void charger();
  }, [charger]);

  if (!session || !masterKey) return null;

  const testid = scope === 'tenant' ? 'cabinet-audit' : 'dossier-audit';

  return (
    <section
      className={
        scope === 'tenant'
          ? 'mt-6 rounded border border-slate-200 p-4 text-left'
          : 'mt-8 border-t border-slate-200 pt-4'
      }
      data-testid={testid}
    >
      <div className="flex items-center gap-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {scope === 'tenant' ? 'Journal du cabinet' : 'Journal du dossier'}
        </h3>
        {bundle && verdict && (
          <button
            type="button"
            onClick={() => exportBundle(bundle, verdict)}
            data-testid="export-audit"
            className="ml-auto text-xs text-blue-900 underline hover:text-blue-700"
          >
            Exporter la pièce
          </button>
        )}
      </div>

      {busy && <p className="mt-2 text-xs text-slate-400">Vérification…</p>}

      {error && (
        <p data-testid="audit-error" className="mt-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {verdict && (
        <>
          <Verdicts verdict={verdict} scope={scope} />

          <ul className="mt-4 space-y-1" data-testid="audit-entries">
            {verdict.entries.length === 0 && (
              <li className="text-sm text-slate-400">Aucune entrée.</li>
            )}
            {verdict.entries.map((e) => {
              const bon =
                e.verdict.hashValid &&
                e.verdict.signatureValid &&
                e.verdict.linkValid !== false;
              return (
                <li
                  key={e.chainIndex}
                  data-testid="audit-entry"
                  className={`flex items-baseline gap-3 rounded px-2 py-1 text-sm ${
                    bon ? '' : 'bg-red-50'
                  }`}
                >
                  <span className="w-10 shrink-0 text-right font-mono text-[10px] text-slate-400">
                    {e.chainIndex}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="text-slate-800">{libelleAction(e.action)}</span>
                    <span className="ml-2 text-xs text-slate-400">
                      {horodatage(e.timestamp)}
                      {e.ipAddress && ` · ${e.ipAddress}`}
                    </span>
                  </span>
                  <span
                    data-testid={bon ? 'entry-ok' : 'entry-ko'}
                    className={`shrink-0 text-xs ${bon ? 'text-emerald-600' : 'font-medium text-red-700'}`}
                    title={
                      bon
                        ? 'Hash recalculé et signature vérifiée dans ce navigateur'
                        : 'ENTRÉE NON VÉRIFIÉE'
                    }
                  >
                    {bon ? 'vérifiée' : 'ALTÉRÉE'}
                  </span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

function Verdicts({
  verdict,
  scope,
}: {
  verdict: ChainVerdict;
  scope: 'dossier' | 'tenant';
}) {
  return (
    <div className="mt-3 space-y-2">
      {/* 1. L'épingle. C'est elle qui décide de la valeur de tout le reste. */}
      {verdict.pinned === 'CHANGED' ? (
        <p
          data-testid="pin-changed"
          className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800"
        >
          <strong>⚠️ LA CLÉ DE SIGNATURE DU JOURNAL A CHANGÉ.</strong> Le serveur
          présente une clé différente de celle vue jusqu&apos;ici. Cela peut être une
          rotation légitime — ou un journal réécrit avec une clé neuve, auquel cas
          tout « vérifierait » sans rien prouver. <strong>N&apos;utilise pas cet
          export sans avoir tiré cela au clair.</strong>
        </p>
      ) : (
        <p
          data-testid="pin-ok"
          className="text-xs text-slate-500"
        >
          Clé du journal{' '}
          <span className="font-mono text-slate-700">{verdict.fingerprint}</span>{' '}
          {verdict.pinned === 'first-seen'
            ? '— vue pour la première fois et épinglée. Compare-la avec un confrère.'
            : '— identique à celle épinglée sur cet appareil.'}
        </p>
      )}

      {/* 2. Le verdict de vérification — celui de CE navigateur. */}
      {verdict.valid ? (
        <p
          data-testid="audit-valid"
          className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800"
        >
          <strong>{verdict.entries.length} entrée(s) vérifiées ici même.</strong>{' '}
          Hash recalculés (BLAKE2b-512) et signatures Ed25519 contrôlées dans ce
          navigateur, avec une implémentation indépendante de celle du serveur. Le
          serveur n&apos;a pas été cru sur parole.
        </p>
      ) : (
        <p
          data-testid="audit-broken"
          className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800"
        >
          <strong>⚠️ JOURNAL ALTÉRÉ.</strong> Anomalie à l&apos;entrée{' '}
          {verdict.brokenAt?.chainIndex} : {verdict.brokenAt?.reason}.
        </p>
      )}

      {/* 3. Ce que ce lot prouve — et ce qu'il ne prouve PAS. */}
      {scope === 'tenant' ? (
        verdict.contiguous ? (
          <p data-testid="audit-complete" className="text-xs text-emerald-700">
            Chaîne <strong>contiguë depuis l&apos;origine</strong> : aucune entrée
            n&apos;a été supprimée. C&apos;est ce niveau de preuve qui vaut en
            défense.
          </p>
        ) : (
          <p data-testid="audit-gap" className="text-xs text-red-700">
            <strong>Chaîne non contiguë</strong> : des entrées manquent. Le journal
            a été amputé.
          </p>
        )
      ) : (
        <p data-testid="audit-partial" className="text-xs text-amber-700">
          Ce journal ne couvre <strong>que ce dossier</strong> : les entrées des
          autres dossiers s&apos;intercalent dans la chaîne du cabinet. Chaque entrée
          montrée est authentique, mais <strong>ce lot ne prouve pas qu&apos;il est
          complet</strong>. Pour cela, exporte la chaîne du cabinet.
        </p>
      )}
    </div>
  );
}
